import {
  CustomerDealDeduplicationEvent,
  PhoneValidationResult,
} from "@/core/entities/customer";
import { DealResubmissionHistoryItem } from "@/core/entities/deal";
import { timestampToDate } from "@/utils/formatting";
import { format, isValid } from "date-fns";

export const SUBMISSION_REVIEW_DATETIME_FORMAT = "MMM d, yyyy h:mm a";

type ReviewTimestamp = Parameters<typeof timestampToDate>[0];

export const parseReviewDate = (value?: ReviewTimestamp | null): Date | null => {
  const date = timestampToDate(value);
  if (!date || !isValid(date)) {
    return null;
  }

  return date;
};

/** Millis for sorting; Firestore may leave nested fields as Timestamp-like objects, not Date. */
export const reviewTimestampToMillis = (
  value?: ReviewTimestamp | null,
): number => {
  const date = parseReviewDate(value);
  if (!date) {
    return Number.NEGATIVE_INFINITY;
  }

  return date.getTime();
};

export const formatDateTime = (value?: ReviewTimestamp | null) => {
  const date = parseReviewDate(value);
  if (!date) {
    return "-";
  }

  return format(date, SUBMISSION_REVIEW_DATETIME_FORMAT);
};

export const formatPhoneType = (value?: PhoneValidationResult["phoneType"]) => {
  if (!value) {
    return "Unknown";
  }

  switch (value) {
    case "voip":
      return "VoIP";
    case "landline":
      return "Landline";
    case "mobile":
      return "Mobile";
    default:
      return "Unknown";
  }
};

export const formatValidationStatus = (
  result?: PhoneValidationResult | null,
) => {
  if (!result) {
    return "Not checked";
  }

  if (result.validationStatus === "skipped") {
    return "Skipped";
  }

  if (result.validationStatus === "error") {
    return "Error";
  }

  return result.valid ? "Valid" : "Invalid";
};

export const getValidationBadgeClassName = (
  result?: PhoneValidationResult | null,
) => {
  if (!result) {
    return "bg-muted text-muted-foreground hover:bg-muted";
  }

  if (result.validationStatus === "error") {
    return "bg-amber-100 text-amber-800 hover:bg-amber-100";
  }

  if (result.validationStatus === "skipped") {
    return "bg-slate-100 text-slate-700 hover:bg-slate-100";
  }

  if (result.valid) {
    return "bg-emerald-100 text-emerald-800 hover:bg-emerald-100";
  }

  return "bg-rose-100 text-rose-800 hover:bg-rose-100";
};

export const formatResponseTime = (responseTimeMs?: number) => {
  if (responseTimeMs === undefined || responseTimeMs === null) {
    return "-";
  }

  return `${responseTimeMs} ms`;
};

export const getLatestDeduplicationEvent = (
  events?: CustomerDealDeduplicationEvent[],
) => {
  if (!events || events.length === 0) {
    return null;
  }

  return [...events].sort(
    (left, right) =>
      reviewTimestampToMillis(right.duplicateSubmissionTimestamp) -
      reviewTimestampToMillis(left.duplicateSubmissionTimestamp),
  )[0];
};

export const getLatestPhoneValidation = (
  history?: PhoneValidationResult[],
  latest?: PhoneValidationResult | null,
) => {
  if (latest) {
    return latest;
  }

  if (!history || history.length === 0) {
    return null;
  }

  return [...history].sort(
    (left, right) =>
      reviewTimestampToMillis(right.validationTimestamp) -
      reviewTimestampToMillis(left.validationTimestamp),
  )[0];
};

export const getLatestResubmission = (
  history?: DealResubmissionHistoryItem[],
) => {
  if (!history || history.length === 0) {
    return null;
  }

  return [...history].sort(
    (left, right) =>
      reviewTimestampToMillis(right.submittedAt) -
      reviewTimestampToMillis(left.submittedAt),
  )[0];
};
